import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import {
	MatterStatementOfAccount,
	StatementOfAccountDefaultTypes,
} from '@models/statementOfAccount';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterStatementofAccountSelectors } from '@state/matter/statementOfAccounts/statementOfAccounts.selectors';
import { ICON } from '@core/util/icon';
import { TestHooks } from './docket-sync.hooks';
import { BaseComponent } from '@core/base.component';
import { TitleSearchFeeSettings } from '@models/titleSearch';
import {
	DocketSyncComponentFees,
	DocketSyncComponentSelector,
} from './docket-sync.selector';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DocketSummaries } from '@models/teranet';

interface VM {
	showNonHstTitle: boolean;
	showNonHstWrit: boolean;
	showHstWrit: boolean;
	sync_parcel_total_hst_appl: boolean;
	sync_writs_total_hst_appl: boolean;
	sync_parcel_total_hst_not_appl: boolean;
	sync_writs_total_hst_not_appl: boolean;
	error: string;
	_summaries: DocketSummaries;
	parcel_total_hst_appl: number;
	writs_total_hst_appl: number;
	parcel_total_hst_not_appl: number;
	writs_total_hst_not_appl: number;
	hstTitle: MatterStatementOfAccount;
	hstWrit: MatterStatementOfAccount;
	nonHstTitle: MatterStatementOfAccount;
	nonHstWrit: MatterStatementOfAccount;
	hstTitleDescription: string;
	nonHstTitleDescription: string;
	hstWritDescription: string;
	nonHstWritDescription: string;
}
const defaultVM: VM = {
	showNonHstTitle: true,
	showNonHstWrit: true,
	showHstWrit: true,
	sync_parcel_total_hst_appl: true,
	sync_writs_total_hst_appl: true,
	sync_parcel_total_hst_not_appl: true,
	sync_writs_total_hst_not_appl: true,
	error: null,
	_summaries: null,
	parcel_total_hst_appl: 0,
	writs_total_hst_appl: 0,
	parcel_total_hst_not_appl: 0,
	writs_total_hst_not_appl: 0,
	hstTitle: null,
	hstWrit: null,
	nonHstTitle: null,
	nonHstWrit: null,
	hstTitleDescription: 'Title Search Charges',
	nonHstTitleDescription: 'Title Search Charges',
	hstWritDescription: 'Writ Search Charges',
	nonHstWritDescription: 'Writ Search Charges',
};

@UntilDestroy()
@Component({
	selector: 'app-docket-sync',
	templateUrl: './docket-sync.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DocketSyncComponent extends BaseComponent<VM> implements OnInit {
	testHooks = TestHooks;
	@Select(DocketSyncComponentSelector.getDocketSyncComponentFees)
	titleSearchFees$: Observable<DocketSyncComponentFees>;

	@SelectSnapshot(SelectedMatter.getTitleSearchFeeSettings)
	titleSearchFeeSettings: TitleSearchFeeSettings;
	@SelectSnapshot(MatterStatementofAccountSelectors.getTitleSearchItems)
	titleSearchItems: MatterStatementOfAccount[];

	@Input() set summaries(_summaries: DocketSummaries) {
		this.VM = {
			_summaries: this.getSummariesByTitleSearchSettings(
				_summaries,
				this.titleSearchFeeSettings,
			),
		};
		if (_summaries) {
			this.VM = {
				parcel_total_hst_appl: this.VM._summaries.parcel.total_hst_appl,
				writs_total_hst_appl: this.VM._summaries.writs.total_hst_appl,
				parcel_total_hst_not_appl: this.VM._summaries.parcel.total_hst_not_appl,
				writs_total_hst_not_appl: this.VM._summaries.writs.total_hst_not_appl,
			};
		}
	}

	public ICON = ICON;
	public StatementOfAccountDefaultTypes = StatementOfAccountDefaultTypes;

	constructor() {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.titleSearchFees$.pipe(untilDestroyed(this)).subscribe((fees) => {
			this.VM = {
				hstTitle: fees.hstTitle,
				hstWrit: fees.hstWrit,
				nonHstTitle: fees.nonHstTitle,
				nonHstWrit: fees.nonHstWrit,
			};
		});

		this.setVM();
	}

	setVM() {
		const descriptions = this.titleSearchItems.reduce(
			(acc, item) => {
				acc[item.id] = item.description;
				return acc;
			},
			{} as Record<StatementOfAccountDefaultTypes, string>,
		);

		const hstTitleDescription =
			descriptions[StatementOfAccountDefaultTypes.HST_TITLE] ||
			defaultVM.hstTitleDescription;
		const nonHstTitleDescription =
			descriptions[StatementOfAccountDefaultTypes.NON_HST_TITLE] || '';
		const hstWritDescription =
			descriptions[StatementOfAccountDefaultTypes.HST_WRIT] || '';
		const nonHstWritDescription =
			descriptions[StatementOfAccountDefaultTypes.NON_HST_WRIT] || '';

		this.VM = {
			showNonHstTitle: !!nonHstTitleDescription,
			showNonHstWrit: !!nonHstWritDescription,
			showHstWrit: !!hstWritDescription,
			hstTitleDescription,
			nonHstTitleDescription,
			hstWritDescription,
			nonHstWritDescription,
		};
	}

	private getSummariesByTitleSearchSettings(
		summaries: DocketSummaries,
		feeSettings: TitleSearchFeeSettings,
	): DocketSummaries {
		const originalParcelTotalHstAppl = summaries.parcel.total_hst_appl;
		const originalParcelTotalHstNotAppl = summaries.parcel.total_hst_not_appl;
		const originalWritsTotalHstAppl = summaries.writs.total_hst_appl;
		const originalWritsTotalHstNotAppl = summaries.writs.total_hst_not_appl;

		if (feeSettings?.applyHstAll && feeSettings?.combineTitleAndWritsFees) {
			// Only show HST_TITLE with all values combined
			summaries.parcel.total_hst_appl +=
				summaries.parcel.total_hst_not_appl +
				summaries.writs.total_hst_appl +
				summaries.writs.total_hst_not_appl;

			summaries.parcel.total_hst_not_appl = 0;
			summaries.writs.total_hst_appl = 0;
			summaries.writs.total_hst_not_appl = 0;
		} else if (feeSettings?.applyHstAll) {
			// Show HST_TITLE and HST_WRIT with combined values
			summaries.parcel.total_hst_appl += summaries.parcel.total_hst_not_appl;
			summaries.writs.total_hst_appl += summaries.writs.total_hst_not_appl;

			summaries.parcel.total_hst_not_appl = 0;
			summaries.writs.total_hst_not_appl = 0;
		} else if (feeSettings?.combineTitleAndWritsFees) {
			// Show HST_TITLE and NON_HST_TITLE with combined writ values
			summaries.parcel.total_hst_appl += summaries.writs.total_hst_appl;
			summaries.parcel.total_hst_not_appl += summaries.writs.total_hst_not_appl;

			summaries.writs.total_hst_appl = 0;
			summaries.writs.total_hst_not_appl = 0;
		} else {
			// Restore original values
			summaries.parcel.total_hst_appl = originalParcelTotalHstAppl;
			summaries.parcel.total_hst_not_appl = originalParcelTotalHstNotAppl;
			summaries.writs.total_hst_appl = originalWritsTotalHstAppl;
			summaries.writs.total_hst_not_appl = originalWritsTotalHstNotAppl;
		}

		return summaries;
	}
}
